import Request from "./Request";

class Api {
  constructor() {
    this.request = new Request();
  }

  /**
   * @param {string} language - user prefered language
   * @param {function} sessionExpiredAction - A redux action which will be dispatch if session is expired
   */
  init({ lang, sessionExpiredAction, accountLockedAction, systemMaintainAction, }) {
    this.request.init({
      lang,
      sessionExpiredAction,
      accountLockedAction,
      systemMaintainAction,
    });
  }

  // ================================ APIs =================================

  getSettings() {
    return this.request.post("/app/setting/get-setting", null, null);
  }

  preRegister(data) {
    return this.request.post("/app/user/pre-register", null, data);
  }

  register({ referral_id, ...data }) {
    if (referral_id) {
      data.referral_id = referral_id;
    }
    return this.request.post("/app/user/register", null, data);
  }

  verifyOtp(data) {
    return this.request.post("/app/otp/verify", null, data);
  }

  prelogin(data) {
    return this.request.post("/app/user/pre-login", null, data);
  }

  login(data) {
    return this.request.post("/app/user/login", null, data);
  }

  logout() {
    return this.request.post("/app/user/logout", null, null);
  }

  forgotPassword(data) {
    return this.request.post("/app/user/forget-password", null, data);
  }

  resetPassword(data) {
    return this.request.post("/app/user/reset-password", null, data);
  }

  updateProfile(data) {
    return this.request.post("/app/user/put-profile", null, data);
  }

  updatePin(data) {
    return this.request.post("/app/user/put-pin", null, data);
  }

  getFriends(data) {
    return this.request.post("/app/user/get-friends", null, data);
  }

  getRooms(data) {
    return this.request.post("/app/room/get-rooms", null, data);
  }

  getMessages(data) {
    return this.request.post("/app/message/get-messages", null, data);
  }

  postMessage(data) {
    return this.request.post("/app/message/post-message", null, data);
  }

  getMessageOptions() {
    return this.request.post("/app/message-option/get-message-options", null, null);
  }

  readMessage(data) {
    return this.request.post("/app/message/read-message", null, data);
  }

  getPopularGames() {
    return this.request.post('/app/game/get-popular-games', null, null);
  }

  getGames() {
    return this.request.post('/app/game/get-games', null, null);
  }

  getChildGames(main_game_id) {
    return this.request.post('/app/game/get-games', null, { main_game_id });
  }

  getGameList() {
    return this.request.post('/app/game/get-child-games', null, null)
  }

  checkGameLossRequests() {
    return this.request.post('/app/loss-request/check', null, null)
  }

  getGameLostRequestList(data) {
    return this.request.post('/app/loss-request/get-requests', null, data);
  }

  requestBalanceLoss(data) {
    return this.request.post('/app/loss-request/post-request', null, data)
  }

  searchGames(search) {
    return this.request.post('/app/game/search-games', null, { search });
  }

  getWinners(data) {
    return this.request.post('/app/game/get-winners', null, data);
  }

  launchGame(data) {
    return this.request.post('/app/game/launch-game', null, data);
  }

  exitGame() {
    return this.request.post('/app/game/exit-game', null, null);
  }

  checkExit() {
    return this.request.post('/app/game/check-exit', null, null);
  }

  postPocketMoney(data) {
    return this.request.post('/app/pocket-money/collect', null, data);
  }

  postCheckPocketMoney(data) {
    return this.request.post('/app/pocket-money/check', null, data);
  }

  getSpinInfo() {
    return this.request.post('/app/spin/get-spin-info', null, null);
  }

  getSpinHistory(data) {
    return this.request.post('/app/spin/get-spin-history', null, data);
  }

  getSpinReward() {
    return this.request.post('/app/spin/get-spin-reward', null, null);
  }

  getRecentHistory() {
    const data = {
      page: 1,
      limit: 4,
    };
    return this.request.post('/app/transaction/get-transactions', null, data);
  }

  getPaymentCategories() {
    return this.request.post('/app/payment-category/get-payment-categories', null, null);
  }

  getUserPaymentCategories() {
    return this.request.post('/app/payment-category/get-available-payment-categories', null, null);
  }

  getSysPayment(data) {
    return this.request.post('/app/sys-payment/get-sys-payment', null, data);
  }

  topup(data) {
    return this.request.post('/app/topup/post-topup', null, data);
  }

  withdraw(data) {
    return this.request.post('/app/withdraw/post-withdraw', null, data);
  }

  getUserPayments() {
    return this.request.post('/app/user-payment/get-user-payments', null, null);
  }

  createUserPayment(data) {
    return this.request.post('/app/user-payment/post-user-payment', null, data);
  }

  editUserPayment(data) {
    return this.request.post('/app/user-payment/put-user-payment', null, data);
  }

  chooseUserPayment(data) {
    return this.request.post('/app/user-payment/choose-user-payment', null, data);
  }

  verifyPin(data) {
    return this.request.post('/app/user/verify-pin', null, data);
  }

  getTransactions({ page, limit, start_date, end_date, type }) {
    const data = {
      page,
      limit,
    }
    if (type) {
      data.type = type;
    }
    if (start_date && end_date) {
      data.start_date = start_date;
      data.end_date = end_date;
    }
    return this.request.post('/app/transaction/get-transactions', null, data);
  }

  getBonusHistory() {
    return this.request.post('/app/transaction/get-bonus-histories', null, null);
  }

  postReferral() {
    return this.request.post('/app/referral/post-referral', null, null);
  }

  getShareLink(data) {
    return this.request.post('/app/share/get-link', null, data)
  }

  getShareTNC(data) {
    return this.request.post('/app/share/get-tnc', null, data)
  }

  viewShareLink(data) {
    return this.request.post('/app/share/view-link', null, data)
  }

  getShareSummary(data) {
    return this.request.post("/app/share/get-summary", null, data)
  }

  getShareHistory(data) {
    return this.request.post('/app/share/get-history', null, data)
  }

  collectShareBonus(data) {
    return this.request.post('/app/share/collect', null, data);
  }

  getCommissionHistory(data) {
    return this.request.post('/app/user/promocode/get-commission-history', null, data);
  }

  getPromoCode() {
    return this.request.post('/app/user/promocode/get-promocode', null, null);
  }

  getFreezeBalances() {
    return this.request.post('/app/game/get-freeze-balances', null, null);
  }

  thawBalance(data) {
    return this.request.post('/app/game/thaw-balance', null, data);
  }

  uploadMedia({ url, fields, file }) {
    const data = new FormData();
    Object.keys(fields).forEach(key => {
      data.append(key, fields[key]);
    });
    data.append('file', file);
    return this.request.post("", null, data, true, url);
  }

  getPresignedUrl({ upload_type, content_type }) {
    const data = {
      upload_type,
      content_type,
    };
    return this.request.post('/presigned-url', null, data);
  }

  /**
   * After a successful login, save session id into
   * request object, which will be send in Headers
   */
  setSessionToken(token) {
    this.request.setSessionToken(token);
  }

  /**
   * When user changes, save user prefered lang into
   * request object, which will be send in Headers
   */
  setUserPreferedLanguage(lang) {
    this.request.setUserPreferedLanguage(lang);
  }
}

const api = new Api();

export default api;
